import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LabelList 
} from 'recharts';
import { format } from 'date-fns';
import { HiOutlineChartBar, HiOutlineInformationCircle } from 'react-icons/hi';
import './MonthlyScores.scss';

const MonthlyScores = ({ scores, isLoading }) => {
  if (isLoading) {
    return (
      <div className="monthly-scores loading">
        <div className="loading-spinner"></div>
        <p>Loading monthly scores...</p>
      </div>
    );
  }
  
  if (!scores || scores.length === 0) {
    return (
      <div className="monthly-scores empty">
        <HiOutlineChartBar className="empty-icon" />
        <h3>No Performance Data Available</h3>
        <p>Your monthly performance data will appear here once you've submitted and received reviews for your weekly reports.</p>
      </div>
    );
  }
  
  // Format data for chart
  const chartData = scores.map(score => ({
    month: format(new Date(score.month), 'MMM yyyy'),
    score: score.average,
    reviewCount: score.reviewCount
  }));
  
  // Calculate overall average
  const overallAverage = scores.reduce((total, item) => total + item.average, 0) / scores.length;
  const formattedAverage = overallAverage.toFixed(1);
  
  // Get score category (1-20 scale)
  const getScoreCategory = (score) => {
    if (score >= 18) return 'Outstanding';
    if (score >= 15) return 'Exceeds Expectations';
    if (score >= 10) return 'Meets Expectations';
    if (score >= 5) return 'Needs Improvement';
    return 'Unsatisfactory';
  };
  
  // Function to get color based on score (1-20 scale)
  const getScoreColor = (score) => {
    if (score >= 18) return '#52c41a';
    if (score >= 15) return '#85ce61';
    if (score >= 10) return '#faad14';
    if (score >= 5) return '#ff9800';
    return '#f5222d';
  };
  
  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="month">{data.month}</p>
          <p className="score">Score: <strong>{data.score.toFixed(1)}</strong></p>
          <p className="reviews">Based on {data.reviewCount} review{data.reviewCount !== 1 ? 's' : ''}</p>
        </div>
      );
    }
    return null;
  };
  
  // Performance breakdown
  const totalReviews = scores.reduce((total, item) => total + item.reviewCount, 0);
  
  return (
    <div className="monthly-scores">
      <div className="score-overview">
        <div className="average-score">
          <div className="score-circle" style={{ backgroundColor: getScoreColor(overallAverage) }}>
            {formattedAverage}
          </div>
          <div className="score-details">
            <h4>Overall Average</h4>
            <p className="category">{getScoreCategory(overallAverage)}</p>
            <p className="info">Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>
      
      <div className="chart-container">
        <h3 className="chart-title">Performance Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 30 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="month" />
            <YAxis domain={[0, 20]} />
            <Tooltip content={<CustomTooltip />} />
            <Bar
              dataKey="score"
              fill="#1890ff"
              radius={[4, 4, 0, 0]}
            >
              <LabelList dataKey="score" position="top" formatter={(value) => value.toFixed(1)} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="score-info">
        <div className="info-header">
          <HiOutlineInformationCircle className="icon" />
          <h3>Understanding Your Scores</h3>
        </div>
        <div className="score-ranges">
          <div className="range-item">
            <span className="range-color" style={{ backgroundColor: '#52c41a' }}></span>
            <span className="range-label">18-20:</span>
            <span className="range-description">Outstanding performance</span>
          </div>
          <div className="range-item">
            <span className="range-color" style={{ backgroundColor: '#85ce61' }}></span>
            <span className="range-label">15-17:</span>
            <span className="range-description">Exceeds expectations</span>
          </div>
          <div className="range-item">
            <span className="range-color" style={{ backgroundColor: '#faad14' }}></span>
            <span className="range-label">10-14:</span>
            <span className="range-description">Meets expectations</span>
          </div>
          <div className="range-item">
            <span className="range-color" style={{ backgroundColor: '#ff9800' }}></span>
            <span className="range-label">5-9:</span>
            <span className="range-description">Needs improvement</span>
          </div>
          <div className="range-item">
            <span className="range-color" style={{ backgroundColor: '#f5222d' }}></span>
            <span className="range-label">1-4:</span>
            <span className="range-description">Unsatisfactory performance</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonthlyScores;